import { forwardRef } from 'react';

import { cn } from '../utils';
import { badgeRecipe } from './Badge.recipe';

import type { BadgeProps } from './types';

/**
 * Design System 2.0 badge.
 *
 * Ported from the Figma component set (file 5aSkE0U4TeWbuIZDvccHjc, node 600:43878). Props mirror
 * the Figma axes, so "Accent Dot" reads straight across as `color` and `appearance` — with the one
 * naming departure `Tag` and `Button` already make, `primary` for Figma's `Accent`.
 *
 * A number the system produces and keeps up to date — unread messages, pending requests, open
 * rooms. DISPLAY-ONLY, and more strictly so than `Tag`: the set has no State axis, so there is no
 * hover, no focus, no disabled and no callback of any kind. A reader can never create, edit or
 * dismiss one. Nothing here renders an interactive element or a tab stop, and that is not an
 * omission to be filled in later — a badge that could be clicked would be a different component.
 *
 * Not a `Tag`. A word, a status ("Draft", "Live") or anything clickable is a `Tag`; this holds
 * digits and a plus sign, which is why it takes a `count` rather than `children`. Roughly twenty
 * files in this frontend are named `…Badge` and render something Tag-shaped, so trust what a thing
 * does rather than what it is called. Distinct again from `Badge` in `common/components`, which is
 * the legacy MUI uppercase text label — that one is a Tag by behaviour despite the name.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer in
 * `packages/design-system/src/styles/design-system.css`, so the same markup is correct in both themes.
 *
 * Width sizes itself: a 20px minimum plus its own padding, so one digit is a circle and three
 * characters stretch it to a pill. Never give an instance a width — forcing one clips the digits.
 * Two digits are the case where the file and its own docs page disagree, and `Badge.recipe.ts`
 * carries the measurements and the call made about them.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ appearance = 'count', 'aria-label': ariaLabel, className, color, count, max = 99, ...rest }, ref) => {
    /**
     * A count of zero renders nothing at all.
     *
     * Enforced here rather than left to the call site, because it is a usage rule the file states
     * as a Don't and there is no reading of it a caller should be able to override: absence IS the
     * message, and a zero is something the reader has to read before learning that nothing
     * happened. Putting it in the component means the four existing tab-badge call sites lose
     * their `!!count &&` guards rather than each keeping a copy of one rule.
     *
     * `appearance` is checked first so a dot — which has no count and means "something is there"
     * regardless of how many — can never be suppressed by this.
     */
    if (appearance === 'count' && count === 0) {
      return null;
    }

    /**
     * The overflow cap, which is the file's `9+` / `99+`.
     *
     * `toLocaleString('en-GB')` because Natter is British English and that is the house rule for
     * every number in the UI. At these bounds it never inserts a separator — `max` is typed to
     * `9 | 99`, so nothing above 99 is ever rendered — and it is here for the rule rather than for
     * the effect, so a later change to the cap cannot reintroduce a bare `1000`.
     */
    const label = count !== undefined && count > max ? `${max}+` : count?.toLocaleString('en-GB');

    return (
      <span
        ref={ref}
        {...rest}
        // `role="img"` is what makes an `aria-label` reach assistive technology at all: a label on
        // a plain `<span>` with no role is not reliably exposed as an accessible name. It is
        // therefore unconditional on the dot, whose whole meaning is colour and presence, and
        // conditional on the count — where the digits are real text and are announced on their
        // own, so a role is only wanted when the caller has supplied better wording to replace
        // them with.
        aria-label={ariaLabel}
        className={cn(badgeRecipe({ appearance, color }), className)}
        role={appearance === 'dot' || ariaLabel !== undefined ? 'img' : undefined}
      >
        {label}
      </span>
    );
  }
);
